import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Plus, Users, MonitorSmartphone, Clock, Search, Mail, Hash } from "lucide-react";
import { buildTenantActions } from "@/components/actions";
import CustomerTag from "@/components/CustomerTag";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTenants, useTenantDeletions } from "@/hooks/useTenants";
import { TENANT_STATUS_CONFIG, TenantStatus } from "@/types/tenant";
import { format } from "date-fns";
import { RequestDeletionDialog } from "@/components/tenants/RequestDeletionDialog";
import { useCompactMode } from "@/hooks/useCompactMode";
import { useIsMobile } from "@/hooks/use-mobile";

export default function TenantsPage() {
  const navigate = useNavigate();
  const { cx } = useCompactMode();
  const isMobile = useIsMobile();
  const { tenants, isLoading, licenseUsage, isLoadingLicenseUsage } = useTenants();
  const { deletions } = useTenantDeletions();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || tenant.tenant_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const hasPendingDeletion = (tenantId: string) => {
    return deletions.some(d => d.customer_id === tenantId && d.status === 'pending');
  };

  const handleRequestDeletion = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setDeletionDialogOpen(true);
  };

  const renderStatusBadge = (status: TenantStatus) => {
    const config = TENANT_STATUS_CONFIG[status] || TENANT_STATUS_CONFIG.active;
    return (
      <Badge variant="secondary" className={`${config.bgColor} ${config.color} border-0`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className={cx("px-6 py-6 space-y-6", "px-6 py-4 space-y-4")}>
        {/* Header */}
        <PageHeader title="Tenant Administration" description="Manage customer tenants and monitor license usage">
          <Button asChild>
            <Link to="/admin/tenants/onboard">
              <Plus className="h-4 w-4 mr-2" />
              Onboard Customer
            </Link>
          </Button>
        </PageHeader>

      {/* License Summary Cards */}
      <div className={cx("grid gap-4 md:grid-cols-3", "grid gap-3 md:grid-cols-3")}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Devices</CardTitle>
            <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingLicenseUsage ? "..." : licenseUsage?.totalDevices || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Enrolled across all tenants
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tenants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingLicenseUsage ? "..." : licenseUsage?.activeTenants || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently active customers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Onboarding</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingLicenseUsage ? "..." : licenseUsage?.pendingTenants || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Tenants being provisioned
            </p>
          </CardContent>
        </Card>
      </div>

        {/* Tenants Table */}
        <Card disableHover>
        <CardHeader>
          <CardTitle>Customer Tenants</CardTitle>
          <CardDescription>
            All customers associated with your partner account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tenants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="creating">Creating</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
                <SelectItem value="migrated">Migrated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery || statusFilter !== "all" 
                ? "No tenants match your filters"
                : "No tenants found. Click 'Onboard Customer' to add your first tenant."}
            </div>
          ) : isMobile ? (
            <div className="space-y-3">
              {filteredTenants.map((tenant) => (
                <div
                  key={tenant.id}
                  onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                  className="flex items-start gap-3 p-4 rounded-lg border bg-card active:bg-muted transition-colors cursor-pointer"
                >
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <CustomerTag customer={tenant.name} />
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{tenant.email || "-"}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {renderStatusBadge(tenant.tenant_status)}
                      {hasPendingDeletion(tenant.id) && (
                        <Badge variant="outline" className="text-warning border-warning/40 bg-warning/10 text-xs">
                          Deletion Pending
                        </Badge>
                      )}
                      <Badge variant="outline" className="gap-1">
                        <MonitorSmartphone className="h-3 w-3" />
                        {tenant.device_count}
                      </Badge>
                      {tenant.subscription_tier && (
                        <Badge variant="outline" className="capitalize">{tenant.subscription_tier}</Badge>
                      )}
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()} className="-mr-2 -mt-1">
                    <ActionsDropdown actions={buildTenantActions({
                      surface: "list",
                      canRequestDeletion: tenant.tenant_status !== 'deleted' && !hasPendingDeletion(tenant.id),
                      onView: () => navigate(`/admin/tenants/${tenant.id}`),
                      onEdit: () => navigate(`/admin/tenants/${tenant.id}/edit`),
                      onRequestDeletion: () => handleRequestDeletion(tenant.id),
                    })} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant Name</TableHead>
                  <TableHead>Admin Email</TableHead>
                  <TableHead>Tenant ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Devices</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.map((tenant) => (
                  <TableRow key={tenant.id} className="cursor-pointer" onClick={() => navigate(`/admin/tenants/${tenant.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <CustomerTag customer={tenant.name} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="truncate max-w-[200px]">{tenant.email || <span className="text-muted-foreground">-</span>}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono truncate max-w-[120px] block">
                        {tenant.id}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {renderStatusBadge(tenant.tenant_status)}
                        {hasPendingDeletion(tenant.id) && (
                          <Badge variant="outline" className="text-warning border-warning/40 bg-warning/10 text-xs">
                            Deletion Pending
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{tenant.device_count}</div>
                      {tenant.expected_device_count && tenant.expected_device_count > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Expected: {tenant.expected_device_count}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.subscription_tier ? (
                        <Badge variant="outline" className="capitalize">{tenant.subscription_tier}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.industry ? (
                        <span className="capitalize">{tenant.industry}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.created_at ? format(new Date(tenant.created_at), "MMM d, yyyy") : "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <ActionsDropdown actions={buildTenantActions({
                        surface: "list",
                        canRequestDeletion: tenant.tenant_status !== 'deleted' && !hasPendingDeletion(tenant.id),
                        onView: () => navigate(`/admin/tenants/${tenant.id}`),
                        onEdit: () => navigate(`/admin/tenants/${tenant.id}/edit`),
                        onRequestDeletion: () => handleRequestDeletion(tenant.id),
                      })} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        </Card>

        {/* Deletion Dialog */}
        <RequestDeletionDialog
          open={deletionDialogOpen}
          onOpenChange={setDeletionDialogOpen}
          tenantId={selectedTenantId}
          tenantName={tenants.find(t => t.id === selectedTenantId)?.name}
        />
      </div>
    </div>
  );
}
